import classNames from 'classnames';
import * as React from 'react';
import { usePlannerContext } from '../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../Planner/Context/PlannerTranslationsContextProvider';
import type { PlannerContentItem } from '../Planner/Context/types';
import { mapPlannerSubjectToOnlineApplicationChoice } from '../Planner/utils';
import type { PlannerPopupProps } from '../PlannerPopup/PlannerPopup';
import { PlannerPopup } from '../PlannerPopup/PlannerPopup';
import { OnlineApplicationProcessState } from '~/Features/OnlineApplication/OnlineApplicationProcessState';
import { FormattedText } from '~/Features/Txt/TxtBase';
import { List } from '~/Partials/List/List';
import { redirect } from '~/Partials/Utils/utils';
import styles from './PlannerContent.module.css';
import { PlannerContentElement } from './PlannerContentElement/PlannerContentElement';
import { PlannerFilter } from './PlannerFilter/PlannerFilter';
import { DesktopPlannerSummary, MobilePlannerSummary } from './PlannerSummary/PlannerSummary';
import { getHoursPerWeek } from './Utils/plannerContentUtils';

export interface PlannerContentProps {
    onlineApplicationFormUrl: string;
    popup: Pick<PlannerPopupProps, 'heading' | 'body'>;
    className?: string;
}

export const PlannerContent: React.FC<PlannerContentProps> = props => {
    const { onlineApplicationFormUrl, popup, className } = props;

    const { currentBookmark, groupedSubjects, allSelectedSubjects, filteredSubjects, timeDetails } =
        usePlannerContext();

    const { description, creditsLabel } = usePlannerTranslations();
    const { singleSubjectsDescription, hoursPerWeekDescriptionFormat } = description;

    const applicationProcess = OnlineApplicationProcessState();

    const subjects = filteredSubjects ?? groupedSubjects;

    const plannerDescription =
        currentBookmark.contextKey === 'subjects'
            ? singleSubjectsDescription
            : FormattedText(hoursPerWeekDescriptionFormat, {
                  studyFormType: currentBookmark.label.toLowerCase(),
                  hours: getHoursPerWeek(currentBookmark.contextKey, timeDetails),
              });

    const handleSummaryActionClick = () => {
        const choices = allSelectedSubjects.map(subject =>
            mapPlannerSubjectToOnlineApplicationChoice(subject, creditsLabel)
        );
        const appendingSucceeded = applicationProcess.append(choices);

        if (appendingSucceeded) {
            redirect(onlineApplicationFormUrl);
        }
    };

    const renderLayoutContentElement = ([key, item]: [string, PlannerContentItem]) => (
        <li className={styles.Element} key={key}>
            <PlannerContentElement {...item} />
        </li>
    );

    const summaryProps = {
        onActionClick: handleSummaryActionClick,
        className: styles.Summary,
    };

    return (
        <div className={classNames(styles.PlannerContent, className)}>
            <div className={styles.Description}>{plannerDescription}</div>
            <PlannerFilter className={styles.Filters} />
            <MobilePlannerSummary {...summaryProps} />
            <DesktopPlannerSummary {...summaryProps} />
            <PlannerPopup {...popup} />
            <List className={styles.Inner} items={Array.from(subjects)} renderItem={renderLayoutContentElement} />
        </div>
    );
};
